import { supabase } from '../lib/supabase';

/**
 * Service for The Stage - Student Dashboard data operations
 * Fetches user progress, XP, popular content for student ecosystem
 */
export const studentDashboardService = {
  /**
   * Get current user's progress overview
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User progress data with XP, level, streak
   */
  async getUserProgress(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('total_lifetime_xp, current_level, daily_streak, longest_streak')?.eq('id', userId)?.single();

      if (error) throw error;

      return {
        totalXp: data?.total_lifetime_xp || 0,
        currentLevel: data?.current_level || 1,
        dailyStreak: data?.daily_streak || 0,
        longestStreak: data?.longest_streak || 0
      };
    } catch (error) {
      console.error('Error fetching user progress:', error);
      throw error;
    }
  },

  /**
   * Get in-progress songs for current user
   * @param {string} userId - User ID
   * @returns {Promise<Array>} List of in-progress songs with completion data
   */
  async getInProgressSongs(userId) {
    try {
      const { data, error } = await supabase?.from('user_lesson_progress')?.select(`
          song_id,
          lesson_section,
          lesson_status,
          video_progress_percentage,
          quiz_completed,
          is_quiz_perfect,
          songs (
            id,
            title,
            musicals (
              id,
              title,
              poster_url
            )
          )
        `)?.eq('user_id', userId)?.in('lesson_status', ['started', 'available'])?.order('updated_at', { ascending: false })?.limit(10);

      if (error) throw error;

      // Transform data to camelCase
      return (data || [])?.map(item => ({
        songId: item?.song_id,
        lessonSection: item?.lesson_section,
        lessonStatus: item?.lesson_status,
        videoProgress: parseFloat(item?.video_progress_percentage) || 0,
        quizCompleted: item?.quiz_completed || false,
        isQuizPerfect: item?.is_quiz_perfect || false,
        songTitle: item?.songs?.title || '',
        musicalTitle: item?.songs?.musicals?.title || '',
        musicalPoster: item?.songs?.musicals?.poster_url || '',
        musicalId: item?.songs?.musicals?.id || ''
      }));
    } catch (error) {
      console.error('Error fetching in-progress songs:', error);
      throw error;
    }
  },

  /**
   * Get recommended next role based on user's progress
   * @param {string} userId - User ID
   * @returns {Promise<Object|null>} Recommended role data
   */
  async getRecommendedRole(userId) {
    try {
      // Get user's completed songs to recommend similar roles
      const { data: completedSongs, error: completedError } = await supabase?.from('user_lesson_progress')?.select('song_id')?.eq('user_id', userId)?.eq('lesson_status', 'completed')?.limit(5);

      if (completedError) throw completedError;

      // Get a role from a different musical than the ones already completed
      const { data, error } = await supabase?.from('roles')?.select(`
          id,
          character_name,
          description,
          voice_type,
          vocal_range,
          is_principal,
          musicals (
            id,
            title,
            poster_url
          )
        `)?.eq('is_principal', true)?.limit(1)?.single();

      if (error) {
        console.warn('No recommended role found:', error);
        return null;
      }

      return {
        roleId: data?.id,
        characterName: data?.character_name,
        description: data?.description,
        voiceType: data?.voice_type,
        vocalRange: data?.vocal_range,
        isPrincipal: data?.is_principal,
        musicalId: data?.musicals?.id || '',
        musicalTitle: data?.musicals?.title || '',
        musicalPoster: data?.musicals?.poster_url || ''
      };
    } catch (error) {
      console.error('Error fetching recommended role:', error);
      return null;
    }
  },

  /**
   * Search across all content types
   * @param {string} query - Search query
   * @param {string} category - Category filter: 'all', 'musicals', 'roles', 'songs'
   * @returns {Promise<Object>} Search results grouped by category
   */
  async searchContent(query, category = 'all') {
    try {
      const searchQuery = `%${query}%`;
      const results = { musicals: [], roles: [], songs: [] };

      // Search musicals
      if (category === 'all' || category === 'musicals') {
        const { data: musicalsData, error: musicalsError } = await supabase?.from('musicals')?.select('id, title, poster_url, description')?.eq('is_active', true)?.ilike('title', searchQuery)?.limit(10);

        if (musicalsError) throw musicalsError;

        results.musicals = (musicalsData || [])?.map(m => ({
          id: m?.id,
          title: m?.title,
          posterUrl: m?.poster_url,
          description: m?.description
        }));
      }

      // Search roles
      if (category === 'all' || category === 'roles') {
        const { data: rolesData, error: rolesError } = await supabase?.from('roles')?.select(`
            id,
            character_name,
            description,
            voice_type,
            musicals (
              id,
              title,
              poster_url
            )
          `)?.ilike('character_name', searchQuery)?.limit(10);

        if (rolesError) throw rolesError;

        results.roles = (rolesData || [])?.map(r => ({
          id: r?.id,
          characterName: r?.character_name,
          description: r?.description,
          voiceType: r?.voice_type,
          musicalId: r?.musicals?.id || '',
          musicalTitle: r?.musicals?.title || '',
          musicalPoster: r?.musicals?.poster_url || ''
        }));
      }

      // Search songs
      if (category === 'all' || category === 'songs') {
        const { data: songsData, error: songsError } = await supabase?.from('songs')?.select(`
            id,
            title,
            musicals (
              id,
              title,
              poster_url
            )
          `)?.ilike('title', searchQuery)?.limit(10);

        if (songsError) throw songsError;

        results.songs = (songsData || [])?.map(s => ({
          id: s?.id,
          title: s?.title,
          musicalId: s?.musicals?.id || '',
          musicalTitle: s?.musicals?.title || '',
          musicalPoster: s?.musicals?.poster_url || ''
        }));
      }

      return results;
    } catch (error) {
      console.error('Error searching content:', error);
      throw error;
    }
  },

  /**
   * Get popular musicals (by favorites count or featured flag)
   * @param {number} limit - Number of results to return
   * @returns {Promise<Array>} List of popular musicals
   */
  async getPopularMusicals(limit = 10) {
    try {
      const { data, error } = await supabase?.from('musicals')?.select(`
          id,
          title,
          poster_url,
          description,
          genre
        `)?.eq('is_active', true)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;

      return (data || [])?.map(m => ({
        id: m?.id,
        title: m?.title,
        posterUrl: m?.poster_url,
        description: m?.description,
        genre: m?.genre
      }));
    } catch (error) {
      console.error('Error fetching popular musicals:', error);
      throw error;
    }
  },

  /**
   * Get popular roles (principal roles from various musicals)
   * @param {number} limit - Number of results to return
   * @returns {Promise<Array>} List of popular roles
   */
  async getPopularRoles(limit = 10) {
    try {
      const { data, error } = await supabase?.from('roles')?.select(`
          id,
          character_name,
          description,
          voice_type,
          vocal_range,
          is_principal,
          musicals (
            id,
            title,
            poster_url
          )
        `)?.eq('is_principal', true)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;

      return (data || [])?.map(r => ({
        id: r?.id,
        characterName: r?.character_name,
        description: r?.description,
        voiceType: r?.voice_type,
        vocalRange: r?.vocal_range,
        isPrincipal: r?.is_principal,
        musicalId: r?.musicals?.id || '',
        musicalTitle: r?.musicals?.title || '',
        musicalPoster: r?.musicals?.poster_url || ''
      }));
    } catch (error) {
      console.error('Error fetching popular roles:', error);
      throw error;
    }
  },

  /**
   * Get popular songs (featured or recent)
   * @param {number} limit - Number of results to return
   * @returns {Promise<Array>} List of popular songs
   */
  async getPopularSongs(limit = 10) {
    try {
      const { data, error } = await supabase?.from('songs')?.select(`
          id,
          title,
          act_number,
          musicals (
            id,
            title,
            poster_url
          )
        `)?.eq('is_featured', true)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;

      // If no featured songs, get recent songs
      if (!data || data?.length === 0) {
        const { data: recentData, error: recentError } = await supabase?.from('songs')?.select(`
            id,
            title,
            act_number,
            musicals (
              id,
              title,
              poster_url
            )
          `)?.order('created_at', { ascending: false })?.limit(limit);

        if (recentError) throw recentError;

        return (recentData || [])?.map(s => ({
          id: s?.id,
          title: s?.title,
          actNumber: s?.act_number,
          musicalId: s?.musicals?.id || '',
          musicalTitle: s?.musicals?.title || '',
          musicalPoster: s?.musicals?.poster_url || ''
        }));
      }

      return (data || [])?.map(s => ({
        id: s?.id,
        title: s?.title,
        actNumber: s?.act_number,
        musicalId: s?.musicals?.id || '',
        musicalTitle: s?.musicals?.title || '',
        musicalPoster: s?.musicals?.poster_url || ''
      }));
    } catch (error) {
      console.error('Error fetching popular songs:', error);
      throw error;
    }
  }
};